import axios from 'axios';
import { useDispatch } from 'react-redux';

import { logout } from '../features/auth/authSlice';

export const apiURL=process.env.REACT_APP_API_BASE_URL || 'http://localhost:11080';
const token_name = 'access_token';

const api = axios.create({
  baseURL: apiURL + '/api',
  headers: { 
    'Content-Type': 'application/json',
    "Cache-Control": "no-cache",
    "Pragma": "no-cache"
  },
  validateStatus: (status) => {
    // accept 2xx and 304 as success
    return (status >= 200 && status < 300) || status === 304;
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(token_name);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  error => Promise.reject(error)
);

// response interceptor: refresh on 401, logout on failure
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Handle token timeout / 401 globally
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error.config;
    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        console.log("### Request is already refreshing token, queuing request.");
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        console.log("*** Refreshing access token now.");
        const refreshToken = localStorage.getItem('refresh_token');
        const res = await axios.post(apiURL +'/api/auth/refresh', {}, {
          headers: { Authorization: `Bearer ${refreshToken}` }
        });
        const newAccessToken = res.data.access_token;

        // update token in store and localStorage
        localStorage.setItem(token_name, newAccessToken);
        api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

        // retry original request
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        console.log("*** Token refreshed successfully, request retrying.");
        return api(originalRequest);
      } catch (refreshError) {
        // refresh failed: logout
        console.log("### Referesh Error - ", refreshError);
        localStorage.removeItem(token_name);
        localStorage.removeItem('refresh_token');
        api.defaults.headers.common.Authorization = null;
        processQueue(refreshError, null);
        // Commented to test window.location.href = '/login';  // or dispatch(logout()) if you have a store ref
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
// May be imported as apiClient
